import { Extension, type Editor } from "@tiptap/core"
import { NodeSelection, Plugin, PluginKey, type EditorState } from "@tiptap/pm/state"

/**
 * Tracks whether the current selection exists only to make drag-and-drop work (a
 * drag-handle click or a drop selecting the dropped node) rather than because the user
 * deliberately selected the node.
 *
 * While true, the plugin keeps the class "drag-mechanics-selection" on the editor root
 * (view.dom); unified-tiptap.css switches --node-selection-highlight to transparent under
 * it, which suppresses every extension's selected-node highlight in one place. The
 * formatting toolbar's eligibility flows through the arbiter's dragging fact, so it
 * doesn't pop for these selections.
 *
 * The state is transactional: any transaction that moves the selection somewhere else
 * clears it, so deliberate selections (clicking a widget, keyboard navigation) highlight
 * as usual with no listener bookkeeping.
 */
export const dragMechanicsSelectionKey = new PluginKey<boolean>("dragMechanicsSelection")

export function isDragMechanicsSelection(state: EditorState): boolean {
  return dragMechanicsSelectionKey.getState(state) ?? false
}

/**
 * Selects the node at `pos`, tagged as drag mechanics (no highlight, no bubble menu).
 *
 * Tiptap's NodeView.onDragStart and ProseMirror's dragstart handler re-select the same
 * node when a drag begins; re-dispatching an identical selection keeps the tag, so
 * calling this from a mousedown or dragstart covers the whole drag.
 */
export function selectNodeAsDragMechanics(editor: Editor, pos: number) {
  editor
    .chain()
    .setNodeSelection(pos)
    .command(({ tr }) => {
      tr.setMeta(dragMechanicsSelectionKey, true)
      return true
    })
    .focus()
    .run()
}

export const DragMechanicsSelection = Extension.create({
  name: "dragMechanicsSelection",

  addProseMirrorPlugins() {
    return [
      new Plugin<boolean>({
        key: dragMechanicsSelectionKey,
        state: {
          init: () => false,
          apply: (tr, value, oldState, newState) => {
            const meta = tr.getMeta(dragMechanicsSelectionKey) as boolean | undefined
            if (meta !== undefined) return meta
            if (!tr.selectionSet) return value

            // Re-dispatching the same selection (tiptap's NodeView.onDragStart and
            // ProseMirror's dragstart handler both re-select the dragged node) keeps
            // the current state.
            if (newState.selection.eq(oldState.selection)) return value

            // Drops select what they insert: our handleSliceDrop tags uiEvent "Drop"
            // and ProseMirror's fallback drop tags "drop". A node-selecting drop is
            // drag mechanics; a text-selecting drop (dragging selected text) is a
            // normal selection — the bubble menu should show over it.
            const uiEvent = tr.getMeta("uiEvent") as unknown
            if (uiEvent === "drop" || uiEvent === "Drop") {
              return newState.selection instanceof NodeSelection
            }

            return false
          },
        },
        props: {
          // ProseMirror keeps this attribute in sync on view.dom across state updates.
          attributes: (state): Record<string, string> =>
            isDragMechanicsSelection(state) ? { class: "drag-mechanics-selection" } : {},
        },
      }),
    ]
  },
})
